'use client';

import { useEffect } from 'react';

/*
 * The last-resort page, shown when the root layout itself fails. It replaces
 * the root layout, so the app's stylesheet and theme are not loaded: the v8
 * palette is restated here, light by default and dark with the system.
 */
const CSS = `
  :root { --c: #FFFFFF; --i: #0C1512; --s: #4E5B57; --m: #5A6864; --t: #006D6D; --o: #FFFFFF; --b: rgba(12,21,18,.13); }
  @media (prefers-color-scheme: dark) { :root { --c: #101614; --i: #ECF1EF; --s: #AFBDB8; --m: #93A29D; --t: #0F7A72; --o: #F2F6F5; --b: rgba(236,241,239,.18); } }
  body { margin: 0; background: var(--c); color: var(--i); font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; }
  .w { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px 16px; box-sizing: border-box; }
  .x { max-width: 420px; width: 100%; }
  h1 { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.028em; }
  p { margin: 10px 0 0; font-size: 13.5px; line-height: 1.6; color: var(--s); }
  .a { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 24px; }
  .btn { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 0 18px; border-radius: 8px; font: inherit; font-size: 14px; font-weight: 500; text-decoration: none; cursor: pointer; white-space: nowrap; }
  .p { background: var(--t); color: var(--o); border: none; }
  .s { background: transparent; color: var(--i); border: 1px solid var(--b); }
  .r { margin-top: 28px; font-size: 12px; color: var(--m); font-family: ui-monospace, monospace; }
`;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Global error', error.digest ?? '');
  }, [error]);

  return (
    <html lang="en">
      <body>
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <div className="w">
          <div className="x">
            <h1>Something went wrong</h1>
            <p>Fitvent didn&apos;t load. Try again, and if it keeps happening, come back in a few minutes.</p>
            <div className="a">
              <button type="button" className="btn p" onClick={reset}>Try again</button>
              {/* A full page load on purpose: the app around this page has failed. */}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a className="btn s" href="/">Go to the homepage</a>
            </div>
            {error.digest && <div className="r">Reference {error.digest}</div>}
          </div>
        </div>
      </body>
    </html>
  );
}
